import React, { useEffect, useRef } from 'react';
import { Box, Button, Typography } from '@mui/material';
import { sanitizeEventDescription } from '../../utils/eventDescription';

interface RichTextEditorProps {
  value: string;
  onChange: (value: string) => void;
  label?: string;
  placeholder?: string;
  minHeight?: number;
}

const RichTextEditor: React.FC<RichTextEditorProps> = ({
  value,
  onChange,
  label,
  placeholder = 'Write something...',
  minHeight = 120,
}) => {
  const editorRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (editorRef.current) {
      const nextValue = sanitizeEventDescription(value);
      if (editorRef.current.innerHTML !== nextValue) {
        editorRef.current.innerHTML = nextValue;
      }
    }
  }, [value]);

  const handleInput = () => {
    if (editorRef.current) {
      onChange(sanitizeEventDescription(editorRef.current.innerHTML));
    }
  };

  const formatText = (command: string, value?: string) => {
    if (!editorRef.current) return;
    editorRef.current.focus();
    document.execCommand(command, false, value);
    handleInput();
  };

  return (
    <Box sx={{ mb: 2 }}>
      {label && (
        <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
          {label}
        </Typography>
      )}
      <Box
        sx={{
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 2,
          overflow: 'hidden',
          bgcolor: 'background.paper',
        }}
      >
        <Box sx={{ display: 'flex', gap: 1, p: 1, borderBottom: '1px solid', borderColor: 'divider', flexWrap: 'wrap' }}>
          <Button size="small" variant="outlined" onMouseDown={(event) => { event.preventDefault(); formatText('bold'); }}>
            Bold
          </Button>
          <Button size="small" variant="outlined" onMouseDown={(event) => { event.preventDefault(); formatText('italic'); }}>
            Italic
          </Button>
          <Button size="small" variant="outlined" onMouseDown={(event) => { event.preventDefault(); formatText('underline'); }}>
            Underline
          </Button>
          <Button size="small" variant="outlined" onMouseDown={(event) => { event.preventDefault(); formatText('insertUnorderedList'); }}>
            Bullet list
          </Button>
        </Box>
        <Box
          ref={editorRef}
          contentEditable
          suppressContentEditableWarning
          onInput={handleInput}
          onBlur={handleInput}
          sx={{
            minHeight,
            p: 2,
            outline: 'none',
            '& p': { margin: 0, mb: 1 },
            '& ul, & ol': { margin: 0, paddingLeft: 2 },
            '& li': { marginBottom: 0.5 },
            '&:empty::before': {
              content: `'${placeholder}'`,
              color: 'text.disabled',
              pointerEvents: 'none',
            },
          }}
        />
      </Box>
    </Box>
  );
};

export default RichTextEditor;
